import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    CircularProgress,
    useTheme,
} from "@mui/material";
import Axios from "axios";
import { tokens } from "../../theme";
import { getAuthHeader } from "../../utils/authToken";
import PrepChapterSessionsModal from "./PrepChapterSessionsModal";
import StudentEttInfoModal from "./StudentEttInfoModal";
import { StudentHeaderContent } from "./prepStudentHeader";

const BASE = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

// hw_status → MUI color token + label. Boss's colors: green on time, orange late (✓ once the
// target is reached, even late), red not taken. null status = open round / no target → neutral.
const HW_STATUS_META = {
    on_time:         { color: "success.main", bar: "success", check: true,  label: "on time" },
    completed_late:  { color: "warning.main", bar: "warning", check: true,  label: "completed late" },
    late_incomplete: { color: "warning.main", bar: "warning", check: false, label: "late — still short" },
    not_taken:       { color: "error.main",   bar: "error",   check: false, label: "not taken" },
};

// QBank homework: rounds (rows) × students (columns). Chapters are per-student
// (weakness-based), so rather than union the chapter rows across students (sparse, grows
// unbounded with the cohort), each student's cell stacks only the chapters that student was
// actually assigned for that round — chapter code + score, full name on hover. Dense, and
// the column count stays at one-per-student.
const PrepHomeworkMatrix = ({ uuid }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [sessionModal, setSessionModal] = useState(null); // { studentId, roundId, chapterId, studentName, chapterName }
    const [infoStudent, setInfoStudent] = useState(null); // student whose ETT-email modal is open

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await Axios.get(`${BASE}/${uuid}/homework-matrix`, {
                headers: { Authorization: getAuthHeader() },
            });
            setData(res.data);
        } catch (e) {
            console.error("Homework matrix load error:", e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [uuid]);

    useEffect(() => {
        load();
    }, [load]);

    // "Ch 3-1. Solving Inequalities" → "Ch 3-1" so a student's cell stays narrow; the full
    // name lives in the tooltip. Falls back to the whole string if there's no ". " separator.
    const chapterCode = (name) => {
        if (!name) return "";
        const idx = name.indexOf(". ");
        return idx > 0 ? name.slice(0, idx) : name;
    };

    // One chapter line inside a student's per-round cell: code (left) + score (right) + bar.
    // Every entry here is an assigned cell, so it's always clickable into the solved questions.
    const renderChapterEntry = (cell, ch, ctx) => {
        const { done = 0, target, pct, complete, mastered, mastery_bar, minutes, on_time, finalized, post_due = 0, hw_status } = cell;
        // Finalized rows render by hw_status (green on time · orange late, ✓ once the target is
        // reached even late · red not taken). Open rounds keep the live green-at-target bar. The
        // status word + "+N after due" live in the tooltip; a muted +N rides beside the score.
        const meta = hw_status ? HW_STATUS_META[hw_status] : null;
        const statusColor = meta ? meta.color : (complete ? "success.main" : "text.primary");
        const barColor = meta ? meta.bar : (complete ? "success" : "primary");
        const mark = (meta ? meta.check : complete) ? " ✓" : "";
        const postDue = Math.max(0, post_due); // clamp defensively (matches the box); should never be < 0
        // Two-tone bar: solid = solved by the due date, hatched = solved after (capped at target).
        const byduePct = target ? Math.min(100, Math.round((100 * Math.min(done, target)) / target)) : 0;
        const latePct = target ? Math.min(100 - byduePct, Math.round((100 * postDue) / target)) : 0;
        const tip = [
            ch.chapter_name || `Chapter ${ch.chapter_id}`,
            `${pct ?? 0}%`,
            `${minutes || 0} min`,
            meta ? meta.label : (finalized ? (on_time ? "on time" : "by due") : null),
            postDue > 0 ? `+${postDue} after due` : null,
            // Per-cell bar (capped at 2× the chapter's question count); flat threshold for old payloads.
            mastered ? `${mastery_bar ?? data?.mastery_threshold ?? 100}+ practiced` : null,
            "click for questions",
        ].filter(Boolean).join(" · ");
        return (
            <Tooltip key={ch.chapter_id} title={tip} arrow>
                <Box sx={{ cursor: "pointer" }} onClick={() => setSessionModal(ctx)}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                textDecoration: "underline dotted",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            {chapterCode(ch.chapter_name)}
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "nowrap", fontWeight: 600, color: statusColor }}>
                            {done}/{target ?? "–"}{mark}
                            {postDue > 0 ? (
                                <Box component="span" sx={{ fontWeight: 500, fontSize: "0.8em", color: "info.main", ml: 0.4 }}>+{postDue}</Box>
                            ) : null}
                        </Typography>
                    </Box>
                    {target ? (
                        <Box sx={{ display: "flex", height: 4, borderRadius: 2, mt: 0.25, overflow: "hidden", backgroundColor: (t) => t.palette.action.hover }}>
                            <Box sx={{ width: `${byduePct}%`, bgcolor: `${barColor}.main` }} />
                            {latePct > 0 ? (
                                <Box sx={{ width: `${latePct}%`, backgroundImage: (t) => `repeating-linear-gradient(45deg, ${t.palette[barColor].main} 0 3px, rgba(0,0,0,0) 3px 6px)` }} />
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            </Tooltip>
        );
    };

    if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    if (error) return <Typography color="error" sx={{ py: 2 }}>Could not load the homework matrix.</Typography>;

    const rounds = data?.rounds || [];
    const students = data?.students || [];
    const cells = data?.cells || {};

    if (!rounds.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No homework rounds have been scheduled for this program.</Typography>;
    if (!students.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No students are enrolled in this program yet.</Typography>;

    // Number rounds PER SUBJECT in due-date order (rounds arrive due-date sorted), so each
    // subject track has its own Round 1, 2, 3. The subject prefix disambiguates the tracks.
    const perSubject = {};
    const numberedRounds = rounds.map((round) => {
        const key = round.prep_program_course_id ?? "_";
        perSubject[key] = (perSubject[key] || 0) + 1;
        return { ...round, _num: perSubject[key] };
    });
    const roundTitle = (round) =>
        round.label || `${round.subject ? `${round.subject} · ` : ""}Round ${round._num}`;

    const stickyHeadCell = { position: "sticky", left: 0, zIndex: 3, backgroundColor: colors.primary[600], width: 220, maxWidth: 220 };
    const stickyBodyCell = { position: "sticky", left: 0, zIndex: 1, backgroundColor: colors.primary[600], width: 220, maxWidth: 220, verticalAlign: "top" };

    return (
        <Box>
            <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[600], maxHeight: "70vh", width: "fit-content", maxWidth: "100%" }}>
                <Table
                    stickyHeader
                    size="small"
                    sx={{
                        width: "auto",
                        "& .MuiTableCell-root": {
                            borderRight: `1px solid ${theme.palette.divider}`,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        "& .MuiTableCell-root:last-child": { borderRight: "none" },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...stickyHeadCell, zIndex: 4 }}>Round</TableCell>
                            {students.map((s) => (
                                <TableCell key={s.student_id} align="center" sx={{ minWidth: 150, fontWeight: 600 }}>
                                    <StudentHeaderContent student={s} onInfo={setInfoStudent} />
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {numberedRounds.map((round) => (
                            <TableRow key={round.id} hover>
                                <TableCell sx={stickyBodyCell}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                                        {roundTitle(round)}
                                    </Typography>
                                    {round.due_date ? (
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            due {fmtDate(round.due_date)}
                                        </Typography>
                                    ) : null}
                                </TableCell>
                                {round.chapters.length === 0 ? (
                                    <TableCell colSpan={students.length} sx={{ color: "text.disabled", fontStyle: "italic" }}>
                                        No chapters assigned yet.
                                    </TableCell>
                                ) : (
                                    students.map((s) => {
                                        // Only the chapters this student was actually assigned this round — no union,
                                        // no blank cells. A student with no assignment this round shows a single "—".
                                        const studentCells = cells?.[s.student_id]?.[round.id] || {};
                                        const chs = round.chapters.filter((ch) => studentCells[ch.chapter_id]);
                                        return (
                                            <TableCell key={s.student_id} sx={{ verticalAlign: "top", minWidth: 150 }}>
                                                {chs.length === 0 ? (
                                                    <Typography variant="caption" sx={{ color: "text.disabled" }}>—</Typography>
                                                ) : (
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                                        {chs.map((ch) => renderChapterEntry(studentCells[ch.chapter_id], ch, {
                                                            studentId: s.student_id,
                                                            roundId: round.id,
                                                            chapterId: ch.chapter_id,
                                                            studentName: s.name || s.email,
                                                            chapterName: ch.chapter_name,
                                                        }))}
                                                    </Box>
                                                )}
                                            </TableCell>
                                        );
                                    })
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ mt: 1.5, maxWidth: 900 }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.7 }}>
                    <b>How to read this grid.</b> Each column is a student; each cell lists that student's assigned chapters for the round.{" "}
                    <b>Ch&nbsp;3-1&nbsp;12/15</b> = chapter code · questions solved <b>by the due date</b> / target (hover the code for the full chapter name).<br />
                    <b>Status:</b>{" "}
                    <b style={{ color: theme.palette.success.main }}>green ✓</b> completed on time ·{" "}
                    <b style={{ color: theme.palette.warning.main }}>orange ✓</b> completed <b>late</b> (reached target after the deadline) ·{" "}
                    <b style={{ color: theme.palette.warning.main }}>orange</b> late and still short of target ·{" "}
                    <b style={{ color: theme.palette.error.main }}>red</b> not taken at all.<br />
                    <b style={{ color: theme.palette.info.main }}>+N</b> = questions solved <b>after</b> the due date (the number on the left counts only work done by the deadline; the bar shows on-time work solid, after-due work hatched). A cell with no color is a round still <b>open</b> — it counts live, with no verdict yet. <b>—</b> = no chapter assigned this round. <b>Click</b> any chapter to see the exact questions solved.
                </Typography>
            </Box>

            <PrepChapterSessionsModal
                open={!!sessionModal}
                onClose={() => setSessionModal(null)}
                uuid={uuid}
                studentId={sessionModal?.studentId}
                roundId={sessionModal?.roundId}
                chapterId={sessionModal?.chapterId}
                studentName={sessionModal?.studentName}
                chapterName={sessionModal?.chapterName}
            />

            <StudentEttInfoModal
                open={!!infoStudent}
                onClose={() => setInfoStudent(null)}
                student={infoStudent}
            />
        </Box>
    );
};

export default PrepHomeworkMatrix;
